import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { getScroller, splitChars, splitWords } from "./text-reveal-utils";

gsap.registerPlugin(ScrollTrigger);

const POST_REVEAL_SELECTOR = ".cw-post-reveal";
const TEXT_SELECTOR = "h1, h2, h3, h4, h5, h6, p, li";
const HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";
const BODY_SELECTOR = "p, li";
const GROUP_SCRUB = 0.9;
const MOBILE_BREAKPOINT = 991;
let resizeTimer;
let resizeReady = false;
let activeAnimations = [];

export const postTextReveal = () => {
	const posts = document.querySelectorAll(POST_REVEAL_SELECTOR);

	if (!posts.length) return;

	initPostTextReveal(posts);
	setupResize();
	ScrollTrigger.refresh();
};

function initPostTextReveal(posts) {
	posts.forEach((post) => {
		const elements = getPostRevealElements(post);

		prepareElements(elements);
		createSectionTimelines(post, elements, getScroller());
	});
}

function setupResize() {
	if (resizeReady) return;

	resizeReady = true;

	window.addEventListener("resize", () => {
		window.clearTimeout(resizeTimer);

		resizeTimer = window.setTimeout(() => {
			const posts = document.querySelectorAll(POST_REVEAL_SELECTOR);

			cleanupAnimations();
			resetPostStyles(posts);
			initPostTextReveal(posts);
			ScrollTrigger.refresh();
		}, 180);
	});
}

function cleanupAnimations() {
	activeAnimations.forEach((animation) => animation.kill());
	activeAnimations = [];
}

function resetPostStyles(posts) {
	posts.forEach((post) => {
		gsap.set(post.querySelectorAll(".char"), {
			clearProps: "opacity"
		});
		gsap.set(post.querySelectorAll(".word"), {
			clearProps: "opacity"
		});
		gsap.set(post.querySelectorAll("li"), {
			clearProps: "opacity"
		});
	});
}

function getPostRevealElements(post) {
	return [...post.querySelectorAll(TEXT_SELECTOR)].filter((element) => {
		return element.closest(POST_REVEAL_SELECTOR) === post;
	});
}

function prepareElements(elements) {
	elements.forEach((element) => {
		if (element.matches(HEADING_SELECTOR) && !element.querySelector(".char")) {
			splitChars(element);
		}

		if (element.matches(BODY_SELECTOR) && !element.querySelector(".word")) {
			splitWords(element);
		}
	});
}

function createSectionTimelines(post, elements, scroller) {
	const groups = getRevealGroups(elements);

	groups.forEach((group) => {
		if (!group.length) return;

		const trigger = group[0];
		const timeline = gsap.timeline({
			scrollTrigger: {
				trigger,
				scroller,
				start: getGroupStart(),
				end: () => getGroupEnd(post, group),
				scrub: GROUP_SCRUB,
				invalidateOnRefresh: true
			}
		});

		group.forEach((element) => {
			const chars = element.querySelectorAll(".char");
			const words = element.querySelectorAll(".word");

			if (element.matches(HEADING_SELECTOR)) {
				if (!chars.length) return;

				addHeadingReveal(timeline, chars);
				return;
			}

			if (element.matches(BODY_SELECTOR)) {
				if (!words.length) return;

				addBodyReveal(timeline, element, words);
			}
		});

		activeAnimations.push(timeline, timeline.scrollTrigger);
	});
}

function getRevealGroups(elements) {
	const groups = [];
	let currentGroup = [];

	elements.forEach((element) => {
		if (element.matches(HEADING_SELECTOR) && currentGroup.length) {
			groups.push(currentGroup);
			currentGroup = [];
		}

		currentGroup.push(element);
	});

	if (currentGroup.length) {
		groups.push(currentGroup);
	}

	return groups;
}

function addHeadingReveal(timeline, chars) {
	timeline.fromTo(
		chars,
		{
			opacity: 0
		},
		{
			opacity: 1,
			stagger: 0.018,
			ease: "sine.out",
			duration: 0.9
		},
		">0.08"
	);
}

function addBodyReveal(timeline, element, words) {
	const position = ">0.08";

	if (element.matches("li")) {
		timeline.to(
			element,
			{
				opacity: 1,
				duration: 0.2,
				ease: "none"
			},
			position
		);
	}

	timeline.fromTo(
		words,
		{
			opacity: 0
		},
		{
			opacity: 1,
			stagger: 0.018,
			ease: "sine.out",
			duration: 0.9
		},
		position
	);
}

function getGroupStart() {
	return isMobileViewport() ? "top 76%" : "top 82%";
}

function getGroupEnd(post, group) {
	const lastElement = group[group.length - 1];
	const distance = lastElement.offsetTop + lastElement.offsetHeight - group[0].offsetTop;
	const revealDistance = distance * 0.85;
	const minDistance = window.innerHeight * (isMobileViewport() ? 0.42 : 0.3);
	const maxDistance = post.offsetHeight;

	return `+=${Math.min(Math.max(revealDistance, minDistance), maxDistance)}`;
}

function isMobileViewport() {
	return window.innerWidth <= MOBILE_BREAKPOINT;
}
